import React, { useState } from 'react';
import axios from 'axios';
import { ButtonSpinner } from '../components/ui/Spinner';
import Input from '../components/ui/Input';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';

function RegisterPage() {
    const { theme, toggleTheme } = useTheme();
    const [form, setForm] = useState({ username: '', email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const navigate = useNavigate();

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);
        try {
            await axios.post('/api/register/', form);
            setSuccess('Account created! You can now log in.');
            setTimeout(() => navigate('/login'), 1200);
        } catch (err) {
            setError(err.response?.data?.detail || err.response?.data?.error || 'Registration failed.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <button
                onClick={toggleTheme}
                className="absolute top-4 right-4 p-2 rounded-lg text-gray-500 dark:text-gray-400 
                         hover:bg-gray-100 dark:hover:bg-gray-800 
                         focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
                {theme === 'dark' ? (
                    <SunIcon className="h-6 w-6" />
                ) : (
                    <MoonIcon className="h-6 w-6" />
                )}
            </button>

            <div className="flex flex-col items-center">
                <img
                    src={theme === 'light' ? '/pokerium_logo_light.png' : '/pokerium_logo.webp'}
                    alt="Pokerium Logo"
                    className="h-[90px] mb-6"
                />

                <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-sm 
                              border border-gray-200 dark:border-gray-700 
                              min-w-[340px]">
                    <h1 className="text-xl sm:text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                        Register
                    </h1>

                    <form onSubmit={handleSubmit}>
                        <Input
                            label="Username"
                            name="username"
                            value={form.username}
                            onChange={handleChange}
                            required
                        />

                        <Input
                            label="Email"
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                            required
                        />

                        <Input
                            label="Password"
                            name="password"
                            type="password"
                            value={form.password}
                            onChange={handleChange}
                            required
                        />

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 bg-primary-600 text-white rounded-lg font-bold
                                     hover:bg-primary-700 focus:outline-none focus:ring-2
                                     focus:ring-primary-500 focus:ring-offset-2
                                     disabled:opacity-50 disabled:cursor-not-allowed
                                     transition-colors duration-200"
                        >
                            {loading ? <ButtonSpinner /> : 'Register'}
                        </button>
                    </form>

                    {error && (
                        <div className="mt-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 
                                      text-red-700 dark:text-red-400 text-sm">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="mt-4 p-4 rounded-lg bg-green-50 dark:bg-green-900/30 
                                      text-green-700 dark:text-green-400 text-sm">
                            {success}
                        </div>
                    )}

                    <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
                        Already have an account?{' '}
                        <RouterLink 
                            to="/login" 
                            className="text-primary-600 dark:text-primary-400 font-semibold 
                                     hover:underline"
                        >
                            Login
                        </RouterLink>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RegisterPage; 